import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePaymentConfig } from "@/hooks/usePaymentConfig";
import { useAppSettings } from "@/hooks/useAppSettings";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { trackBeginCheckout, getGaClientId, getGaSessionId, getFbTrackingIds } from "@/lib/analytics";
import { X, Lock, Loader2, ArrowRight } from "lucide-react";
import { Link } from "wouter";

type Tier = "A" | "B" | "C" | "D";
const TIERS: Tier[] = ["A", "B", "C", "D"];

interface OutOfCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nome del profilo con cui si sta chattando, per personalizzare il testo. */
  partnerName?: string | null;
  /**
   * Modalita' "azione bloccata": l'utente ha provato a fare qualcosa che costa
   * piu' di quanto ha. Passando il costo e il saldo, il popup dice quanto manca
   * e propone il pacchetto piu' piccolo che basta, invece del listino completo:
   * chi voleva UNA foto sta cercando di sbloccare quella, non di scegliere un
   * abbonamento.
   */
  shortfall?: {
    /** Crediti richiesti dall'azione. */
    cost: number;
    /** Crediti attualmente disponibili. */
    available: number;
    /** Cosa stava facendo, per la riga esplicativa. Default: "richiedere una foto". */
    actionLabel?: string;
  } | null;
}

/**
 * Popup mostrato quando i crediti finiscono durante una chat. Prezzi, crediti
 * e badge arrivano tutti da `payment_config` (usePaymentConfig), quindi
 * restano allineati alla pagina /premium-status e all'admin senza duplicare
 * numeri nel componente.
 */
export function OutOfCreditsModal({ open, onOpenChange, partnerName, shortfall }: OutOfCreditsModalProps) {
  const { config } = usePaymentConfig();
  const { getSetting } = useAppSettings();
  // Stessa lettura di getMessageCost() lato server: 0 e' valido (messaggi
  // gratis), valori non numerici ricadono sul default 1.
  const parsedMessageCost = parseInt(String(getSetting("credits_per_message", "")), 10);
  const messageCost = Number.isFinite(parsedMessageCost) && parsedMessageCost >= 0 ? parsedMessageCost : 1;
  const { toast } = useToast();
  const [buyingTier, setBuyingTier] = useState<Tier | null>(null);

  const formatPrice = (price: number) => price.toFixed(2).replace(".", ",");

  // In modalita' shortfall si mostra UN pacchetto: il piu' economico che copre
  // davvero il mancante, non semplicemente il primo del listino — se all'utente
  // mancano 300 crediti, offrirgliene 100 non sbloccherebbe l'azione.
  // Se nessuno basta (mancante enorme), si ripiega sul piu' capiente.
  const missing = shortfall ? Math.max(0, shortfall.cost - shortfall.available) : 0;
  const suggestedTier: Tier | null = (() => {
    if (!shortfall) return null;
    const available = TIERS.filter((t) => config.packages[t]);
    if (!available.length) return null;
    const credits = (t: Tier) => config.packages[t]!.credits ?? 0;
    const covering = available.filter((t) => credits(t) >= missing);
    // Il piu' piccolo fra quelli che bastano; se nessuno basta, il piu' grande.
    return covering.length
      ? covering.reduce((a, b) => (credits(b) < credits(a) ? b : a))
      : available.reduce((a, b) => (credits(b) > credits(a) ? b : a));
  })();

  // Stesso flusso di checkout di premium-status: crea la sessione e redirige.
  const handleBuy = async (tier: Tier) => {
    if (buyingTier) return;
    setBuyingTier(tier);
    const pkg = config.packages[tier];
    if (pkg) {
      trackBeginCheckout({
        id: `credits_${tier}`,
        name: `${pkg.credits} Crediti`,
        price: pkg.price,
        category: "credits",
        // Le due modalita' dello stesso popup sono intenti diversi: "azione
        // bloccata" nasce da qualcosa che l'utente stava gia' facendo.
        source: shortfall ? "insufficient_for_action" : "out_of_credits",
      });
    }
    try {
      const res = await apiRequest("POST", "/api/payments/checkout", {
        tier,
        gaClientId: getGaClientId(),
        gaSessionId: getGaSessionId(),
        ...getFbTrackingIds(),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.redirect) {
        const stripeRes = await apiRequest("POST", data.redirect, { tier });
        const stripeData = await stripeRes.json();
        if (stripeData.url) {
          window.location.href = stripeData.url;
          return;
        }
        toast({
          title: "Errore",
          description: stripeData.error || "Impossibile creare la sessione di pagamento",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Errore",
          description: data.error || "Impossibile creare la sessione di pagamento",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      console.error("Payment error:", err);
      toast({
        title: "Errore",
        description: err?.message || "Servizio pagamenti non disponibile",
        variant: "destructive",
      });
    } finally {
      setBuyingTier(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Il DialogContent di base e' chiaro e ha una X propria: qui forziamo il
          tema scuro del mockup e nascondiamo quella X ([&>button]:hidden) per
          non averne due. */}
      <DialogContent
        className="max-w-[560px] w-[calc(100%-2rem)] p-0 gap-0 border-0 bg-transparent shadow-none [&>button]:hidden"
      >
        <div className="relative rounded-3xl border border-pink-500 bg-[#1a0d1a] bg-gradient-to-b from-[#241028] to-[#150a17] px-4 pb-5 pt-6 sm:px-5 sm:pb-6 sm:pt-8 shadow-[0_0_40px_rgba(236,72,153,0.35)] max-h-[90vh] overflow-y-auto">
          <DialogPrimitive.Close
            className="absolute right-3 top-3 sm:right-4 sm:top-4 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border border-white/25 text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </DialogPrimitive.Close>

          {/* Icona: solo in modalita' shortfall, dove indica COSA e' bloccato
              (la foto). Nel popup generico non c'e' un oggetto singolo da
              raffigurare e occuperebbe spazio senza dire nulla. */}
          {shortfall && (
            <div className="mb-3 flex justify-center" aria-hidden="true">
              <LockedPhotoIcon />
            </div>
          )}

          <DialogTitle className="text-center text-[22px] sm:text-[28px] font-extrabold leading-tight text-white">
            {shortfall ? (
              <>
                Ti mancano{" "}
                <span className="text-pink-500">
                  {missing} {missing === 1 ? "credito" : "crediti"}
                </span>
              </>
            ) : partnerName ? (
              <>
                Continua a chattare<span className="hidden sm:inline"><br /></span>{" "}
                con <span className="text-pink-500">{partnerName}</span>
              </>
            ) : (
              <>
                Ti servono <span className="text-pink-500">più crediti?</span>
              </>
            )}
          </DialogTitle>
          {/* max-w + text-balance: senza, la frase riempie tutta la larghezza e
              spezza dove capita, lasciando righe sbilanciate. */}
          <DialogDescription className="mx-auto mt-1.5 max-w-[52ch] text-balance text-center text-[13px] sm:text-[15px] leading-snug text-slate-300">
            {shortfall ? (
              <>
                <span className="block">
                  Per {shortfall.actionLabel || "richiedere una foto"} servono{" "}
                  <span className="font-bold text-pink-500">
                    {shortfall.cost} crediti.
                  </span>
                </span>
                <span className="block">
                  Hai {shortfall.available}{" "}
                  {shortfall.available === 1 ? "credito disponibile" : "crediti disponibili"}.
                </span>
              </>
            ) : (
              <>
                <span className="block font-semibold text-white">Hai terminato i tuoi crediti.</span>
                Scegli un pacchetto per inviare nuovi messaggi, ricevere foto e vivere
                la conversazione senza interruzioni.
              </>
            )}
          </DialogDescription>

          {messageCost > 0 && (
            <p className="mt-2 text-center text-[13px] sm:text-[15px] font-bold text-white">
              1 messaggio = {messageCost} {messageCost === 1 ? "credito" : "crediti"}
            </p>
          )}

          {/* Pacchetti crediti, dalla config */}
          <div className="mt-4 space-y-2 sm:mt-5 sm:space-y-2.5">
            {(shortfall && suggestedTier ? [suggestedTier] : TIERS).map((tier) => {
              const pkg = config.packages[tier];
              if (!pkg) return null;
              const highlighted = shortfall ? true : !!pkg.label;
              const isBuying = buyingTier === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  onClick={() => handleBuy(tier)}
                  disabled={!!buyingTier}
                  className={`relative flex w-full items-center gap-2.5 rounded-2xl border px-2.5 py-2 sm:gap-3 sm:px-3 sm:py-3 text-left transition-colors disabled:opacity-60 ${
                    highlighted
                      ? "border-pink-500 bg-pink-500/10"
                      : "border-white/10 bg-white/[0.04] hover:border-white/25"
                  }`}
                >
                  {pkg.label && !shortfall && (
                    <span className="absolute -top-2 left-4 whitespace-nowrap rounded-md bg-gradient-to-r from-pink-500 to-rose-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      {pkg.label}
                    </span>
                  )}
                  <CoinStack tier={tier} />
                  <span className="min-w-0 flex-1">
                    <span className="block whitespace-nowrap text-[24px] sm:text-[26px] font-extrabold leading-tight text-white">
                      {pkg.credits} crediti
                    </span>
                  </span>
                  {/* Prezzo e bottone: impilati a destra sotto i 640px (sulla stessa
                      riga il prezzo finiva tagliato, "€ 16,9..."), affiancati da sm. */}
                  <span className="flex shrink-0 flex-col items-center gap-1.5 sm:flex-row sm:gap-3">
                    {/* Prezzo: niente corsivo (non e' convenzione per i prezzi),
                        tabular-nums cosi' le cifre restano incolonnate tra le righe. */}
                    <span className="whitespace-nowrap text-[17px] sm:text-[19px] font-bold leading-none tracking-tight text-white tabular-nums">
                      €{formatPrice(pkg.price)}
                    </span>
                    <span className="flex min-w-[96px] sm:min-w-[108px] items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 px-3 py-2 sm:py-2.5 text-[13px] sm:text-[15px] font-bold text-white shadow-[0_4px_15px_rgba(236,72,153,0.35)]">
                      {isBuying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Acquista
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Via d'uscita dal pacchetto suggerito: chi vuole ricaricare di piu'
              (o semplicemente scegliere) va al listino completo. Senza questo
              il popup a pacchetto singolo sarebbe un vicolo cieco. */}
          {shortfall && suggestedTier && (
            <div className="mt-3 text-center">
              <Link
                href="/premium-status"
                onClick={() => onOpenChange(false)}
                className="inline-flex items-center gap-1.5 text-[13px] sm:text-[15px] font-bold text-pink-500 underline underline-offset-4 transition-colors hover:text-pink-400"
              >
                Vedi gli altri pacchetti
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          <div className="mt-4 flex items-center justify-center gap-2 text-[11px] sm:text-[13px] text-slate-400">
            <Lock className="h-3.5 w-3.5" />
            Pagamento sicuro e discreto
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Pila di monete dorate accanto a ogni pacchetto: cresce con il tier. */
function CoinStack({ tier }: { tier: Tier }) {
  const layers = { A: 2, B: 3, C: 4, D: 5 }[tier] ?? 2;
  return (
    <span className="flex h-9 w-10 sm:h-11 sm:w-12 shrink-0 items-end justify-center">
      <svg viewBox="0 0 46 42" className="h-full w-full" aria-hidden="true">
        {Array.from({ length: layers }).map((_, i) => (
          <g key={i} transform={`translate(0 ${30 - i * 5})`}>
            <ellipse cx="16" cy="8" rx="14" ry="5" fill="#d99a1f" />
            <ellipse cx="16" cy="6.5" rx="14" ry="5" fill="#fbbf24" />
          </g>
        ))}
        <circle cx="32" cy="26" r="11" fill="#d99a1f" />
        <circle cx="32" cy="24.5" r="11" fill="#fcd34d" />
        <path
          d="M32 30c-3.4-2.4-5.4-4.4-5.4-6.7a3.1 3.1 0 015.4-2 3.1 3.1 0 015.4 2c0 2.3-2 4.3-5.4 6.7z"
          fill="#f59e0b"
        />
      </svg>
    </span>
  );
}

/**
 * Due foto sovrapposte con un lucchetto: dice a colpo d'occhio COSA e' bloccato
 * quando il popup nasce da un'azione rifiutata per crediti insufficienti.
 * Disegnata inline per restare coerente col rosa del popup senza caricare altro.
 */
function LockedPhotoIcon() {
  return (
    <svg viewBox="0 0 96 80" className="h-16 w-20 sm:h-20 sm:w-24" aria-hidden="true">
      {/* Foto dietro, solo contorno: da' profondita' senza rubare attenzione */}
      <rect
        x="26" y="6" width="46" height="36" rx="5"
        fill="none" stroke="#f472b6" strokeWidth="3"
        transform="rotate(-8 49 24)"
      />
      {/* Foto davanti, piena */}
      <rect x="20" y="18" width="48" height="38" rx="5" fill="#ec4899" />
      <circle cx="34" cy="31" r="4.5" fill="#fbcfe8" />
      <path d="M24 52l13-15 10 11 6-6 11 10z" fill="#fbcfe8" />
      {/* Lucchetto: cerchio pieno col colore di sfondo del popup, cosi' si
          stacca dalla foto senza bisogno di un bordo */}
      <circle cx="68" cy="52" r="15" fill="#241028" />
      <circle cx="68" cy="52" r="12" fill="#ec4899" />
      <rect x="62.5" y="50" width="11" height="9" rx="2" fill="#fbcfe8" />
      <path
        d="M65 50v-2.5a3 3 0 016 0V50"
        fill="none" stroke="#fbcfe8" strokeWidth="2.2" strokeLinecap="round"
      />
    </svg>
  );
}
